import { type ElementType, Fragment, type ReactNode } from 'react';

import classNames from 'classnames';

import { AudioPlayerSerializer } from '@/audioPlayerElement';
import { LoginModalElement } from '@/loginModalElement';
import { RecommendationsModalElement } from '@/recommendationsModalElement';
import { RSSFeedElement } from '@/rssFeedElement';

import {
  AccordionContainerElement,
  AccordionContentElement,
  AccordionElement,
  AccordionTitleElement,
} from '../accordionElement';
import { AuthorImagesElement } from '../authorImagesElement';
import { AuthorsElement } from '../authorsElement/Authors';
import { BlockquoteElement, CaptionElement, QuoteElement } from '../blockquoteElement';
import { BreadcrumbsElement } from '../breadcrumbsElement';
import { ButtonElement } from '../buttonElement';
import { CodeBlockElement } from '../codeBlockElement';
import { ColumnElement } from '../columnElement';
import { ColumnsElement } from '../columnsElement';
import { ContactFormElement } from '../contactFormElement';
import { ContainerElement } from '../containerElement';
import { ContentGateElement } from '../contentGateElement';
import { DividerElement } from '../dividerElement';
import { EmbedElement } from '../embedElement';
import { FigcaptionElement } from '../figcaptionElement';
import { FooterElement } from '../footerElement';
import {
  GenericEmbedDescriptionElement,
  GenericEmbedElement,
  GenericEmbedHostElement,
  GenericEmbedTitleElement,
} from '../genericEmbedElement/GenericEmbed';
import {
  Heading1Element,
  Heading2Element,
  Heading3Element,
  Heading4Element,
  Heading5Element,
  Heading6Element,
} from '../headingElement';
import { IconElement } from '../iconElement';
import { ImageElement } from '../imageElement';
import { BulletedListElement, NumberedListElement } from '../listElement';
import { ListItemElement } from '../listItemElement';
import { LoginElement } from '../loginElement';
import { ParagraphSerializer } from '../paragraphElement';
import { PodcastPlatformsElement } from '../podcastPlatformsElement';
import { PodcastTranscriptElement } from '../podcastTranscriptElement';
import { PostCommentFormElement } from '../postCommentFormElement';
import { PostCommentsElement } from '../postCommentsElement';
import { PostCommentsFeedElement } from '../postCommentsFeedElement';
import { PostElement } from '../postElement/Post';
import { PostShareElement } from '../postShareElement';
import { PricingElement } from '../pricingElement';
import { ProductReviewsElement } from '../productReviewsElement';
import { ProductsElement } from '../productsElement/Products';
import { RecommendationsElement } from '../recommendationsElement/Recommendations';
import { ResetPasswordElement } from '../resetPasswordElement';
import { RichTextElement } from '../richTextElement';
import { RootElement } from '../rootElement';
import { SectionElement } from '../sectionElement';
import { SignupElement } from '../signupElement';
import { SignupModalElement } from '../signupModalElement';
import { SocialsElement } from '../socialsElement';
import { SpacerElement } from '../spacerElement';
import { SurveyElement } from '../surveyElement';
import { SwitchElement } from '../switchElement';
import { TagsElement } from '../tagsElement/Tags';
import { TestimonialsElement } from '../testimonialsElement/Testimonials';
import { SerializeTextLeaf } from '../textLeaf';
import type { BasicNode, DreamRenderElementProps } from '../types';
import { UpdatePasswordElement } from '../updatePasswordElement';
import type { GetElementProps } from './Serializer.types';

const componentMap: Record<string, ElementType> = {
  audioPlayer: AudioPlayerSerializer,
  blockquoteFigure: BlockquoteElement,
  button: ButtonElement,
  breadcrumbs: BreadcrumbsElement,
  codeBlock: CodeBlockElement,
  columns: ColumnsElement,
  column: ColumnElement,
  container: ContainerElement,
  divider: DividerElement,
  figcaption: FigcaptionElement,
  footer: FooterElement,
  image: ImageElement,
  signupModal: SignupModalElement,
  listItem: ListItemElement,
  orderedList: NumberedListElement,
  bulletList: BulletedListElement,
  paragraph: ParagraphSerializer,
  spacer: SpacerElement,
  quote: QuoteElement,
  quoteCaption: CaptionElement,
  icon: IconElement,
  genericEmbed: GenericEmbedElement,
  genericEmbedTitle: GenericEmbedTitleElement,
  genericEmbedDescription: GenericEmbedDescriptionElement,
  genericEmbedHost: GenericEmbedHostElement,
  accordion: AccordionElement,
  accordionContainer: AccordionContainerElement,
  accordionTitle: AccordionTitleElement,
  accordionContent: AccordionContentElement,
  section: SectionElement,
  contentGate: ContentGateElement,
  post: PostElement,
  signup: SignupElement,
  recommendations: RecommendationsElement,
  testimonials: TestimonialsElement,
  authors: AuthorsElement,
  contactForm: ContactFormElement,
  survey: SurveyElement,
  resetPassword: ResetPasswordElement,
  socials: SocialsElement,
  tags: TagsElement,
  updatePassword: UpdatePasswordElement,
  login: LoginElement,
  pricing: PricingElement,
  postComments: PostCommentsElement,
  postCommentForm: PostCommentFormElement,
  postCommentsFeed: PostCommentsFeedElement,
  products: ProductsElement,
  productReviews: ProductReviewsElement,
  podcastPlatforms: PodcastPlatformsElement,
  podcastTranscript: PodcastTranscriptElement,
  embed: EmbedElement,
  authorImages: AuthorImagesElement,
  switch: SwitchElement,
  postShare: PostShareElement,
  loginModal: LoginModalElement,
  recommendationsModal: RecommendationsModalElement,
  richText: RichTextElement,
  rss_feed: RSSFeedElement, // TODO: check what this implies
};

type MappedNodeType = keyof typeof componentMap;

type RenderMappedElementProps = {
  Component: ElementType;
  key: string;
  node: GetElementProps['node'];
  className: string;
  children: GetElementProps['children'];
  dataAttributes?: DreamRenderElementProps<BasicNode>['attributes'];
  isLive?: boolean;
  isPreviewing?: boolean;
};

const renderMappedElement = ({
  Component,
  key,
  node,
  className,
  children,
  dataAttributes,
  isLive,
  isPreviewing,
}: RenderMappedElementProps): ReactNode => {
  const sharedProps = {
    key,
    element: node,
    className,
    attributes: dataAttributes,
  };

  if (isPreviewing) {
    return (
      <Component {...sharedProps} isPreviewing>
        {children}
      </Component>
    );
  }

  if (typeof isLive === 'boolean') {
    return (
      <Component {...sharedProps} isLive={isLive}>
        {children}
      </Component>
    );
  }

  return <Component {...sharedProps}>{children}</Component>;
};

const getHeadingComponent = (level?: number): ElementType => {
  switch (level) {
    case 1:
      return Heading1Element;
    case 2:
      return Heading2Element;
    case 3:
      return Heading3Element;
    case 4:
      return Heading4Element;
    case 5:
      return Heading5Element;
    default:
      return Heading6Element;
  }
};

export function getElement({
  node,
  rootClassName,
  rootStyle,
  children,
  key,
  isShowModals,
  disableEmbeds,
  jsonPath,
}: GetElementProps): ReactNode {
  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node} />;
  }

  const dataAttributes = jsonPath ? { 'data-json-path': jsonPath.join(',') } : undefined;
  const renderMappedType = ({
    type,
    className,
    isLive,
    isPreviewing,
  }: {
    type: MappedNodeType;
    className: string;
    isLive?: boolean;
    isPreviewing?: boolean;
  }) =>
    renderMappedElement({
      Component: componentMap[type],
      key,
      node,
      className,
      children,
      dataAttributes,
      isLive,
      isPreviewing,
    });

  switch (node.type) {
    case 'audioPlayer':
    case 'paragraph':
    case 'listItem':
    case 'bulletList':
    case 'orderedList':
    case 'codeBlock':
    case 'spacer':
    case 'divider':
    case 'image':
    case 'figcaption':
    case 'blockquoteFigure':
    case 'quote':
    case 'quoteCaption':
    case 'button':
    case 'columns':
    case 'column':
    case 'container':
    case 'footer':
    case 'genericEmbed':
    case 'genericEmbedTitle':
    case 'genericEmbedDescription':
    case 'genericEmbedHost':
    case 'accordion':
    case 'accordionContainer':
    case 'recommendations':
    case 'testimonials':
    case 'authors':
    case 'contactForm':
    case 'survey':
    case 'resetPassword':
    case 'socials':
    case 'tags':
    case 'switch':
    case 'updatePassword':
    case 'login':
    case 'pricing':
    case 'products':
    case 'productReviews':
    case 'postComments':
    case 'accordionTitle':
    case 'accordionContent':
    case 'podcastPlatforms':
    case 'podcastTranscript':
    case 'post':
    case 'postShare':
    case 'signup':
    case 'section':
    case 'icon':
    case 'authorImages':
    case 'breadcrumbs':
    case 'richText':
    case 'rss_feed':
      return renderMappedType({
        type: node.type as MappedNodeType,
        className: classNames(rootClassName, `dream-${node.type}`),
        isLive: node.type === 'socials',
      });
    case 'embed':
      if (disableEmbeds) {
        return (
          <div
            key={key}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              borderRadius: '12px',
              border: '4px dashed #d1d5db',
              backgroundColor: '#f9fafb',
              padding: '80px 32px',
              color: '#9ca3af',
            }}
          >
            <span style={{ fontSize: '72px', fontWeight: 500 }}>Code block</span>
            <span style={{ fontSize: '48px', textAlign: 'center', lineHeight: 1.4 }}>
              Embed preview is disabled here.
              <br />
              Expand to see the full preview.
            </span>
          </div>
        );
      }
      return renderMappedType({
        type: node.type,
        className: classNames(rootClassName, `dream-${node.type}`),
      });
    case 'recommendationsModal':
    case 'signupModal':
    case 'loginModal':
      if (isShowModals) {
        return renderMappedType({
          type: node.type,
          className: classNames(rootClassName, `dream-${node.type}`),
          isPreviewing: true,
        });
      }
      return <Fragment key={key}>{children}</Fragment>;
    case 'heading': {
      return renderMappedElement({
        Component: getHeadingComponent(node.attrs?.level as number),
        key,
        node,
        className: `dream-${node.type}`,
        children,
        dataAttributes,
      });
    }
    case 'doc': {
      return (
        <RootElement element={node} className={classNames(rootClassName, `dream-${node.type}`)} style={rootStyle}>
          {children}
        </RootElement>
      );
    }
    case 'hardBreak':
      return <br />;
    default:
      return <Fragment key={key}>{children}</Fragment>;
  }
}
